// Command Center, Deck layout (POC). The morning brief as a card-game client:
// fires in play, today's prompts in your hand, snoozed prompts in the deck,
// dismissed ones on the discard pile, and the selected prompt enlarged.
// Data and actions: useMorningBrief.js. Toggle: CommandCenterSwitch.jsx.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlarmClock, ArrowRight, Bot, CheckCircle2, Compass, Flag, Flame, FlaskConical, Hourglass, Inbox, Layers, NotebookPen, Plug, Scale, Search, Trash2, Undo2 } from 'lucide-react';
import { entityPath } from '../lib/productInsights';
import { BrainStatus } from '../components/brain/BrainUI';
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import { DAY, TYPE_NAMES, age, born, clock, humanDates, safeName, shortDate, stamp, useMorningBrief, verb } from './useMorningBrief';
import './CommandCenterDeck.css';

// Each kind of prompt has its own card frame.
const KINDS = {
  issue: { type: 'issues', label: 'Critical issue', tag: 'Fire', frame: 'fire' },
  readiness: { type: 'initiatives', label: 'Initiative', tag: 'Initiative', frame: 'initiative' },
  validated: { type: 'problems', label: 'Problem', tag: 'Problem', frame: 'problem' },
  stronger: { type: 'problems', label: 'Problem', tag: 'Problem', frame: 'problem' },
  stale: { type: 'problems', label: 'Stale evidence', tag: 'Stale', frame: 'stale' },
  decision: { type: 'decisions', label: 'Decision review', tag: 'Decision', frame: 'decision' },
  experiment: { type: 'experiments', label: 'Experiment', tag: 'Experiment', frame: 'experiment' },
};
// Each frame's emblem, printed large behind the number in the art window.
const EMBLEMS = { fire: Flame, initiative: Flag, problem: Search, decision: Scale, experiment: FlaskConical, stale: Hourglass, signal: Inbox };
// The featured card's fact, named for what it is.
const FACT_LABELS = { issue: 'Record', readiness: 'Readiness', validated: 'Evidence score', stronger: 'Evidence score', stale: 'Evidence age', decision: 'Review due', experiment: 'Ended' };
const factValue = meta => String(meta || '').replace(/^(Readiness|Score|Evidence|Due|Ended)\s+/, '');
const TYPE_FRAMES = { issues: 'fire', initiatives: 'initiative', problems: 'problem', evidence: 'problem', decisions: 'decision', experiments: 'experiment', results: 'experiment', learnings: 'experiment', hypotheses: 'experiment', objectives: 'stale', inbox: 'signal', reminders: 'stale' };
// Urgency on a fixed ramp: four pips for a fire, one for stale evidence.
const PIPS = { 0: 4, 1: 3, 2: 2, 3: 1 };

// The number that matters most for a prompt, printed in the card's art window.
function statFor(s, now) {
  const { item, kind, record } = s;
  const match = re => item.detail?.match(re)?.[1];
  const today = now.toISOString().slice(0, 10);
  switch (kind.prefix) {
    case 'issue': return { big: age(born(record) || stamp(record), now) || '—', label: 'open' };
    case 'readiness': return { big: match(/Readiness (\d+)%/) ? `${match(/Readiness (\d+)%/)}%` : '—', label: 'ready' };
    case 'validated':
    case 'stronger': return { big: match(/Evidence score (\d+)/) || '—', label: 'evidence score' };
    case 'stale': return { big: match(/(\d+) days old/) ? `${match(/(\d+) days old/)}d` : '—', label: 'since evidence' };
    case 'decision': {
      if (!record?.review_date) return { big: 'Due', label: 'review' };
      const late = Math.round((new Date(`${today}T12:00:00`) - new Date(`${record.review_date}T12:00:00`)) / DAY);
      return late > 0 ? { big: `${late}d`, label: 'overdue' } : { big: shortDate(record.review_date), label: 'review due' };
    }
    case 'experiment': return { big: record?.end_date ? shortDate(record.end_date) : 'Due', label: 'ended' };
    default: return { big: '—', label: '' };
  }
}

const Pips = ({ n }) => <span className="dk-pips" title={`Urgency ${n} of 4`} aria-label={`Urgency ${n} of 4`}>{[1, 2, 3, 4].map(i => <i key={i} className={i <= n ? 'on' : ''} />)}</span>;

// A card in the hand or in play: select it, play it, or triage from the keyboard.
function MiniCard({ s, now, selected, first, style, onSelect, onAct, variant = 'hand' }) {
  const { item, kind, isNew } = s;
  const stat = statFor(s, now);
  return <article tabIndex={-1} data-strip={item.id} data-guide={first ? 'brief-first' : undefined} aria-label={`${kind.label}: ${item.title}`}
    className={`dk-card dk-mini is-${variant} frame-${kind.frame}${selected ? ' is-selected' : ''}`}
    style={{ ...style, viewTransitionName: safeName(item.id) }} onClick={onSelect} onFocus={onSelect}>
    <div className="dk-card-face">
      <header className="dk-card-head"><span>{kind.tag}</span><Pips n={PIPS[item.priority] ?? 2} /></header>
      <h3>{item.title}</h3>
      <div className="dk-mini-stat"><strong>{stat.big}</strong><span>{stat.label}</span></div>
      <footer className="dk-mini-foot">
        {isNew && <span className="dk-new">New</span>}
        <Link className="dk-play" to={item.href} onClick={e => { e.stopPropagation(); onAct(); }} aria-label={item.action}>{verb(item.action)}<ArrowRight aria-hidden="true" /></Link>
      </footer>
    </div>
  </article>;
}

// The selected prompt, enlarged: tilts toward the pointer; new cards carry foil.
// The card carries the title, the key number and the reason; the inspector
// beside it carries the facts and the actions.
function FeaturedCard({ s, now, busy, onAct, onSnooze, onDismiss, onWake }) {
  const ref = useRef(null);
  const { item, kind, meta, status, isNew, record } = s;
  const stat = statFor(s, now);
  const Emblem = EMBLEMS[kind.frame] || Inbox;
  const until = status.snoozedUntil && new Date(status.snoozedUntil);
  const tilt = e => {
    if (e.pointerType !== 'mouse' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    ref.current.style.transform = `perspective(900px) rotateY(${x * 10}deg) rotateX(${-y * 8}deg)`;
    ref.current.style.setProperty('--foil-x', `${(x + 0.5) * 100}%`);
  };
  const untilt = () => { if (ref.current) ref.current.style.transform = ''; };
  const updated = age(stamp(record), now);
  const when = until && `${until.toLocaleDateString(undefined, { weekday: 'short' })} ${until.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  return <div className="dk-featured" aria-live="polite">
    <article ref={ref} className={`dk-card dk-big frame-${kind.frame}${isNew ? ' is-foil' : ''}`} onPointerMove={tilt} onPointerLeave={untilt}>
      <header className="dk-card-head"><span>{kind.tag}</span>{isNew && <span className="dk-new">New</span>}<Pips n={PIPS[item.priority] ?? 2} /></header>
      <h2>{item.title}</h2>
      <div className="dk-art"><Emblem className="dk-emblem" aria-hidden="true" /><strong>{stat.big}</strong><span>{stat.label}</span></div>
      <p className="dk-textbox">{humanDates(item.detail)}</p>
    </article>
    <div className="dk-inspector">
      <dl className="dk-facts">
        <div><dt>Type</dt><dd>{kind.label}</dd></div>
        {meta && <div><dt>{FACT_LABELS[kind.prefix] || 'Status'}</dt><dd>{factValue(meta)}</dd></div>}
        {until && <div><dt>In deck until</dt><dd>{when}</dd></div>}
        {updated && <div><dt>Record updated</dt><dd>{updated} ago</dd></div>}
        <div><dt>Source</dt><dd>{item.source || 'Calculated from your records'}</dd></div>
      </dl>
      <div className="dk-actions">
        <Link className="dk-btn dk-btn-play" to={item.href} onClick={onAct}>{item.action}<ArrowRight aria-hidden="true" /></Link>
        <div className="dk-actions-row">
          {until
            ? <button type="button" className="dk-btn" onClick={onWake} disabled={busy}><Undo2 aria-hidden="true" />Back to hand</button>
            : <button type="button" className="dk-btn" onClick={onSnooze} disabled={busy}><AlarmClock aria-hidden="true" />Back to deck <kbd>S</kbd></button>}
          {!until && <button type="button" className="dk-btn dk-btn-discard" onClick={onDismiss} disabled={busy}><Trash2 aria-hidden="true" />Discard <kbd>E</kbd></button>}
        </div>
      </div>
    </div>
  </div>;
}

export default function CommandCenterDeck({ layoutSwitch = null }) {
  const b = useMorningBrief({ kinds: KINDS, movingClass: 'deck-moving' });
  const { data, loading, workspace, guide, now, sinceText, error, busy, strips, fires, today, watch, clearedToday, arrivals, record, remove, onKeyDown } = b;
  const [selectedId, setSelectedId] = useState(null);
  const [toDeck, setToDeck] = useState(null);
  const [deckOpen, setDeckOpen] = useState(false);
  const [dealt, setDealt] = useState(false);
  const deckRef = useRef(null);
  const rootRef = useRef(null);
  // Keyboard-driven selection never animates; pointer-driven selection may.
  useEffect(() => {
    const mark = value => () => { if (rootRef.current) rootRef.current.dataset.input = value; };
    const keys = mark('keys'); const pointer = mark('pointer');
    document.addEventListener('keydown', keys); document.addEventListener('pointerdown', pointer);
    return () => { document.removeEventListener('keydown', keys); document.removeEventListener('pointerdown', pointer); };
  }, []);
  useEffect(() => { if (!loading && !dealt) { const t = setTimeout(() => setDealt(true), 1200); return () => clearTimeout(t); } }, [loading, dealt]);
  useEffect(() => {
    if (!deckOpen) return undefined;
    const close = e => { if (e.key === 'Escape' || (e.type === 'mousedown' && !deckRef.current?.contains(e.target))) setDeckOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [deckOpen]);

  const live = list => list.filter(s => !s.status.stamped);
  const inPlay = live(fires);
  const hand = live(today);
  const deck = live(watch);
  const discarded = strips.filter(s => s.status.stamped);
  const visible = [...inPlay, ...hand, ...deck];
  const selected = visible.find(s => s.item.id === selectedId) || inPlay[0] || hand[0] || deck[0];
  const firstId = (inPlay[0] || hand[0] || deck[0])?.item.id;
  const handIds = new Set([...inPlay, ...hand].map(s => s.item.id));

  const select = id => () => setSelectedId(id);
  const nextAfter = id => { const i = visible.findIndex(s => s.item.id === id); return (visible[i + 1] || visible[i - 1])?.item.id || null; };
  const handlers = s => ({
    busy: busy.has(s.item.id),
    onAct: () => guide?.act(s.item.id),
    onSnooze: () => { setToDeck(s.item.id); setSelectedId(nextAfter(s.item.id)); record(s.item, 'snooze'); },
    onDismiss: () => { setSelectedId(nextAfter(s.item.id)); record(s.item, 'dismiss'); },
    onWake: () => { setSelectedId(s.item.id); remove(s.item, s.status.records.map(f => f.id)); },
  });
  const focusZone = selector => document.querySelector(selector)?.focus();
  const fan = (i, n) => {
    const mid = (n - 1) / 2;
    const step = n > 6 ? 3 : 5;
    return { '--rot': `${(i - mid) * step}deg`, '--lift': `${Math.min(16, Math.abs(i - mid) ** 2 * 3)}px`, '--i': i, zIndex: i + 1 };
  };
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  // The card just sent to the deck flies onto the pile's top card.
  const deckTopName = toDeck && !handIds.has(toDeck) ? safeName(toDeck) : undefined;
  const lastDiscard = discarded[0];

  return <div ref={rootRef} className={`deck${dealt ? '' : ' is-dealing'}`} style={{ '--dk-back-mark': `url(${process.env.PUBLIC_URL || ''}/spcc-logo.png)` }}>
    <header className="dk-bar">
      <div className="dk-title"><h1>Your morning brief</h1><span>{[workspace?.name, now.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })].filter(Boolean).join(' · ')}</span></div>
      <nav className="dk-chips" aria-label="Brief totals">
        <button type="button" className={`dk-chip${inPlay.length ? ' is-fire' : ''}`} onClick={() => focusZone('.dk-inplay [data-strip]')}>{loading ? '—' : plural(inPlay.length, 'fire', 'fires')} in play</button>
        <button type="button" className="dk-chip" onClick={() => focusZone('.dk-hand [data-strip]')}>{loading ? '—' : hand.length} in hand</button>
        <button type="button" className="dk-chip" onClick={() => setDeckOpen(true)}>{loading ? '—' : deck.length} in deck</button>
        <span className="dk-chip is-quiet">{loading ? '—' : clearedToday} discarded today</span>
      </nav>
      <div className="dk-tools">{layoutSwitch}<Link className="dk-btn dk-btn-capture" to="/inbox" data-guide="capture-signal"><Inbox aria-hidden="true" />Capture a signal</Link></div>
    </header>
    <div className="dk-notices"><BrainStatus quietRefresh />{error && <div className="v2-error" role="alert">{error}</div>}</div>

    <div className="dk-body">
      <div className="dk-table" onKeyDown={onKeyDown}>
        <section className="dk-inplay" aria-label="In play">
          <h2 className="dk-zone-label">In play</h2>
          {loading ? <div className="dk-ghost-row" aria-hidden="true"><span /><span /></div>
            : inPlay.length
              ? <div className="dk-inplay-row">{inPlay.map(s => <MiniCard key={s.item.id} s={s} now={now} variant="inplay" selected={selected?.item.id === s.item.id} first={s.item.id === firstId} onSelect={select(s.item.id)} onAct={() => guide?.act(s.item.id)} />)}</div>
              : <p className="dk-calm"><CheckCircle2 aria-hidden="true" />No fires. The table is calm.</p>}
        </section>

        <section className="dk-stage" aria-label="Selected card">
          {loading ? <div className="dk-stage-empty"><span className="dk-card dk-back" aria-hidden="true" /><p>Shuffling your records…</p></div>
            : selected ? <FeaturedCard key={selected.item.id} s={selected} now={now} {...handlers(selected)} />
              : <div className="dk-stage-empty"><span className="dk-card dk-back" aria-hidden="true" /><div><h2>{data.problems.length ? 'A clean table.' : 'Start with a customer problem.'}</h2><p>Capture a signal in Inbox, or add a problem in Discovery. Your brief deals new cards from the evidence and initiatives you save.</p></div></div>}
        </section>

        <section className="dk-handzone" aria-label="Your hand">
          <div className="dk-pile-wrap" ref={deckRef}>
            <button type="button" className="dk-pile dk-pile-deck" aria-expanded={deckOpen} aria-label={`Deck: ${deck.length} snoozed or stale`} onClick={() => setDeckOpen(o => !o)}>
              <span className="dk-card dk-back" /><span className="dk-card dk-back" style={{ viewTransitionName: deckTopName }} />
              <span className="dk-pile-count"><Layers aria-hidden="true" />{deck.length}</span>
            </button>
            {deckOpen && <div className="dk-deck-list" role="dialog" aria-label="Deck">
              <h2>Deck <span>Snoozed and stale</span></h2>
              {deck.length ? <ul>{deck.map(s => <li key={s.item.id} className={`frame-${s.kind.frame}`}>
                <button type="button" onClick={() => { setSelectedId(s.item.id); setDeckOpen(false); }}><i aria-hidden="true" /><span><strong>{s.item.title}</strong><small>{s.status.snoozedUntil ? `Snoozed until ${new Date(s.status.snoozedUntil).toLocaleDateString(undefined, { weekday: 'short' })}` : s.meta || s.kind.label}</small></span></button>
              </li>)}</ul> : <p>The deck is empty.</p>}
            </div>}
          </div>

          <div className="dk-hand" style={{ '--overlap': `${hand.length <= 4 ? -14 : hand.length <= 6 ? -36 : -64}px` }}>
            {loading ? [0, 1, 2, 3].map(i => <span key={i} className="dk-card dk-back dk-hand-ghost" style={fan(i, 4)} aria-hidden="true" />)
              : hand.length ? hand.map((s, i) => <MiniCard key={s.item.id} s={s} now={now} style={fan(i, hand.length)} selected={selected?.item.id === s.item.id} first={s.item.id === firstId} onSelect={select(s.item.id)} onAct={() => guide?.act(s.item.id)} />)
                : <p className="dk-calm">Your hand is empty.</p>}
          </div>

          <div className="dk-pile dk-pile-discard" aria-label={`Discard pile: ${clearedToday} today`}>
            {lastDiscard
              ? <div className={`dk-card dk-mini is-discard frame-${lastDiscard.kind.frame}`} style={{ viewTransitionName: safeName(lastDiscard.item.id) }}>
                <div className="dk-card-face"><header className="dk-card-head"><span>{lastDiscard.kind.tag}</span></header><h3>{lastDiscard.item.title}</h3><span className="dk-void" role="status">Discarded</span>
                  <button type="button" className="dk-undo" onClick={() => remove(lastDiscard.item, [lastDiscard.status.recordId])} disabled={!lastDiscard.status.recordId}><Undo2 aria-hidden="true" />Undo <kbd>Z</kbd></button></div>
              </div>
              : <span className="dk-card dk-slot" aria-hidden="true"><Trash2 /></span>}
            <span className="dk-pile-count">{clearedToday}</span>
          </div>
        </section>
      </div>

      <aside className="dk-log" aria-labelledby="dk-log-title">
        <h2 id="dk-log-title">{sinceText ? `Since ${sinceText}` : 'Recent changes'}</h2>
        {arrivals.length
          ? <ol>{arrivals.map(n => <li key={`${n.type}:${n.id}`} className={`frame-${TYPE_FRAMES[n.type] || 'signal'}`}>
            <Link to={entityPath(n.type, n.id)}><i aria-hidden="true" /><span><strong>{n.label}</strong><small>{n.change || `Updated ${TYPE_NAMES[n.type] || n.type}`}</small></span><time dateTime={n.at}>{clock(n.at, now)}</time></Link>
          </li>)}</ol>
          : <p className="dk-log-empty">{loading ? 'Reading your records…' : 'Nothing changed since then.'}</p>}
        <nav className="dk-shortcuts" aria-label="Shortcuts">
          {guide?.activation?.path === 'jira' && !data.imports.length && <Link to="/onboarding"><Plug aria-hidden="true" />Setup &amp; integrations</Link>}
          <Link to="/autopilot"><Bot aria-hidden="true" />Agent team</Link>
          <Link to="/brain"><Compass aria-hidden="true" />Product Brain</Link>
          <Link to="/decisions"><NotebookPen aria-hidden="true" />Record a decision</Link>
          <Link to="/learning"><CheckCircle2 aria-hidden="true" />Review learning</Link>
        </nav>
        <WorkspaceInsights area="command_center" />
      </aside>
    </div>

    <footer className="dk-status">
      <span>{loading ? 'Dealing…' : `${plural(inPlay.length + hand.length, 'card', 'cards')} on the table · ${deck.length} in deck · ${clearedToday} discarded`}</span>
      <span className="dk-keys" aria-hidden="true"><kbd>J</kbd><kbd>K</kbd> pick <kbd>Enter</kbd> play <kbd>S</kbd> to deck <kbd>E</kbd> discard <kbd>Z</kbd> undo</span>
    </footer>
  </div>;
}
